import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { PageTransition, FadeIn, StaggerContainer, StaggerItem } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ChevronRight, User, ShoppingBag, Lock, LogOut, 
  Loader2, Package, Clock, CheckCircle, AlertCircle, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pending_payment: { label: 'En attente paiement', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  paid_mock: { label: 'Payée (historique test)', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  failed: { label: 'Paiement échoué', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  expired: { label: 'Session expirée', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  confirmed: { label: 'Confirmée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  in_progress: { label: 'En cours', color: 'bg-purple-100 text-purple-700', icon: Package },
  completed: { label: 'Terminée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700', icon: AlertCircle }
};

export default function MonComptePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setIsLoadingOrders(true);
    try {
      const response = await axios.get(`${API_URL}/orders/`);
      setOrders(response.data);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Mot de passe actuel requis';
    }
    if (!passwordForm.newPassword) {
      errors.newPassword = 'Nouveau mot de passe requis';
    } else if (passwordForm.newPassword.length < 6) {
      errors.newPassword = 'Minimum 6 caractères';
    }
    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Confirmation requise';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setIsChangingPassword(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        confirm_password: passwordForm.confirmPassword
      });
      
      toast.success('Mot de passe modifié avec succès');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur lors du changement de mot de passe';
      toast.error(message);
      if (message.includes('actuel')) {
        setPasswordErrors({ currentPassword: message });
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-gradient-to-b from-[var(--champagne)]/30 to-white">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--gold)]/10 flex items-center justify-center"
          >
            <User size={32} className="text-[var(--gold)]" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-2 text-[var(--charcoal)]"
          >
            Bonjour, {user.first_name}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[var(--charcoal)]/60"
          >
            {user.email}
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Mon Compte</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-[1000px] mx-auto">
          <Tabs defaultValue="orders">
            <TabsList className="w-full flex justify-start mb-8 bg-transparent gap-2 border-b border-[var(--champagne)] pb-4">
              <TabsTrigger
                value="orders"
                className="px-6 py-3 text-xs uppercase tracking-[0.15em] font-medium data-[state=active]:bg-[var(--charcoal)] data-[state=active]:text-white rounded-none transition-all flex items-center gap-2"
              >
                <ShoppingBag size={14} />
                Mes Commandes
              </TabsTrigger>
              <TabsTrigger
                value="security"
                className="px-6 py-3 text-xs uppercase tracking-[0.15em] font-medium data-[state=active]:bg-[var(--charcoal)] data-[state=active]:text-white rounded-none transition-all flex items-center gap-2"
              >
                <Lock size={14} />
                Sécurité
              </TabsTrigger>
            </TabsList>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <FadeIn>
                <div className="bg-white border border-[var(--champagne)]">
                  <div className="p-6 border-b border-[var(--champagne)]">
                    <h2 className="font-['Cormorant_Garamond'] text-xl text-[var(--charcoal)]">
                      Historique des commandes
                    </h2>
                  </div>
                  
                  {isLoadingOrders ? (
                    <div className="p-12 text-center">
                      <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[var(--gold)]" />
                      <p className="text-sm text-[var(--charcoal)]/60">Chargement...</p>
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="p-12 text-center">
                      <ShoppingBag size={48} className="mx-auto mb-4 text-[var(--charcoal)]/20" />
                      <p className="text-[var(--charcoal)]/60 mb-4">Aucune commande pour le moment</p>
                      <Link
                        to="/press-on-nails"
                        className="inline-block px-6 py-3 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.15em] hover:bg-[var(--dark-gold)] transition-colors"
                      >
                        Commander maintenant
                      </Link>
                    </div>
                  ) : (
                    <StaggerContainer className="divide-y divide-[var(--champagne)]">
                      {orders.map((order, index) => {
                        const statusInfo = statusLabels[order.status] || statusLabels.pending;
                        const StatusIcon = statusInfo.icon;
                        return (
                          <StaggerItem key={order.id}>
                            <Link 
                              to={`/mon-compte/commandes/${order.id}`}
                              className="block p-6 hover:bg-[var(--champagne)]/10 transition-colors"
                            >
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="font-mono text-sm text-[var(--charcoal)]/50">
                                      #{order.id.slice(0, 8).toUpperCase()}
                                    </span>
                                    <span className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${statusInfo.color}`}>
                                      <StatusIcon size={12} />
                                      {statusInfo.label}
                                    </span>
                                  </div>
                                  <p className="text-sm text-[var(--charcoal)]/60">
                                    {formatDate(order.created_at)}
                                  </p>
                                  {order.items && (
                                    <p className="text-sm text-[var(--charcoal)]/70 mt-1">
                                      {order.items.length} article{order.items.length > 1 ? 's' : ''}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right flex items-center gap-4">
                                  <div>
                                    <p className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)]">
                                      {order.total_price?.toFixed(2) || '—'}€
                                    </p>
                                    {order.delivery_fee === 0 && (
                                      <p className="text-xs text-green-600">Livraison offerte</p>
                                    )}
                                  </div>
                                  <ChevronRight size={20} className="text-[var(--charcoal)]/30" />
                                </div>
                              </div>
                            </Link>
                          </StaggerItem>
                        );
                      })}
                    </StaggerContainer>
                  )}
                </div>
              </FadeIn>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security">
              <FadeIn>
                <div className="bg-white border border-[var(--champagne)]">
                  <div className="p-6 border-b border-[var(--champagne)]">
                    <h2 className="font-['Cormorant_Garamond'] text-xl text-[var(--charcoal)]">
                      Modifier mon mot de passe
                    </h2>
                  </div>
                  
                  <form onSubmit={handlePasswordChange} className="p-6 space-y-4 max-w-[400px]">
                    <div>
                      <Label>Mot de passe actuel</Label>
                      <div className="relative">
                        <Input
                          type={showPasswords.current ? 'text' : 'password'}
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className={passwordErrors.currentPassword ? 'border-red-500 pr-10' : 'pr-10'}
                          data-testid="input-current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40 hover:text-[var(--charcoal)]"
                        >
                          {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {passwordErrors.currentPassword && (
                        <p className="text-red-500 text-xs mt-1">{passwordErrors.currentPassword}</p>
                      )}
                    </div>

                    <div>
                      <Label>Nouveau mot de passe</Label>
                      <div className="relative">
                        <Input
                          type={showPasswords.new ? 'text' : 'password'}
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                          className={passwordErrors.newPassword ? 'border-red-500 pr-10' : 'pr-10'}
                          data-testid="input-new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40 hover:text-[var(--charcoal)]"
                        >
                          {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {passwordErrors.newPassword && (
                        <p className="text-red-500 text-xs mt-1">{passwordErrors.newPassword}</p>
                      )}
                      <p className="text-xs text-[var(--charcoal)]/50 mt-1">Minimum 6 caractères</p>
                    </div>

                    <div>
                      <Label>Confirmer le nouveau mot de passe</Label>
                      <div className="relative">
                        <Input
                          type={showPasswords.confirm ? 'text' : 'password'}
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className={passwordErrors.confirmPassword ? 'border-red-500 pr-10' : 'pr-10'}
                          data-testid="input-confirm-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40 hover:text-[var(--charcoal)]"
                        >
                          {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {passwordErrors.confirmPassword && (
                        <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={isChangingPassword}
                      className="w-full py-3 bg-[var(--gold)] text-white text-sm uppercase tracking-[0.15em] hover:bg-[var(--dark-gold)] rounded-none"
                      data-testid="btn-change-password"
                    >
                      {isChangingPassword ? (
                        <>
                          <Loader2 size={16} className="animate-spin mr-2" />
                          Modification...
                        </>
                      ) : (
                        'Modifier le mot de passe'
                      )}
                    </Button>
                  </form>
                </div>

                {/* Logout Section */}
                <div className="mt-6 bg-white border border-[var(--champagne)] p-6">
                  <h3 className="font-medium text-[var(--charcoal)] mb-4">Déconnexion</h3>
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="text-red-500 border-red-500 hover:bg-red-50 rounded-none"
                  >
                    <LogOut size={16} className="mr-2" />
                    Se déconnecter
                  </Button>
                </div>
              </FadeIn>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </PageTransition>
  );
}
